import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Spin,
  TimePicker,
  InputNumber,
  Divider,
  Switch,
} from "antd";
import {
  ShopOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  DollarOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT, fetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { RestaurantPublicInfo, UpdateRestaurantPublicInfoRequest } from "./types";

const { Text } = Typography;

type RestaurantPublicInfoEditorProps = {
  restaurantId: string;
};

export const RestaurantPublicInfoEditor: React.FC<RestaurantPublicInfoEditorProps> = ({
  restaurantId,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { updateSuccess, notifyError } = useNotification();
  const [info, setInfo] = useState<RestaurantPublicInfo | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const refreshInfo = () => {
    getFetchT<{ restaurant_public_info: RestaurantPublicInfo }>(
      `${API_URL}/v1/r/${restaurantId}/admin/restaurant_public_info`,
      (result) => {
        setInfo(result.restaurant_public_info);
        const data = result.restaurant_public_info;
        form.setFieldsValue({
          address: data.address || "",
          open_time:
            data.open_second !== null && data.open_second !== undefined
              ? dayjs().startOf("day").add(data.open_second, "second")
              : null,
          close_time:
            data.close_second !== null && data.close_second !== undefined
              ? dayjs().startOf("day").add(data.close_second, "second")
              : null,
          instagram_url: data.instagram_url || "",
          google_url: data.google_url || "",
          yelp_url: data.yelp_url || "",
          facebook_url: data.facebook_url || "",
          tip_1: data.default_tip_percentages?.[0] ?? null,
          tip_2: data.default_tip_percentages?.[1] ?? null,
          tip_3: data.default_tip_percentages?.[2] ?? null,
          order_url: data.order_url || "",
          delivery_url: data.delivery_url || "",
          listed: !!data.listed,
          coming_soon: !!data.coming_soon,
        });
      },
      (err) => {
        notifyError(t("failed_to_fetch_restaurant_public_info"), err);
        setInfo({} as RestaurantPublicInfo);
      }
    );
  };

  useEffect(() => {
    refreshInfo();
  }, [restaurantId]);

  const onFinish = (values: {
    address: string;
    open_time?: dayjs.Dayjs | null;
    close_time?: dayjs.Dayjs | null;
    instagram_url?: string;
    google_url?: string;
    yelp_url?: string;
    facebook_url?: string;
    tip_1?: number | null;
    tip_2?: number | null;
    tip_3?: number | null;
    order_url?: string;
    delivery_url?: string;
    listed?: boolean;
    coming_soon?: boolean;
  }) => {
    setLoading(true);

    const tipPercentages: number[] = [];
    if (values.tip_1 !== null && values.tip_1 !== undefined) tipPercentages.push(values.tip_1);
    if (values.tip_2 !== null && values.tip_2 !== undefined) tipPercentages.push(values.tip_2);
    if (values.tip_3 !== null && values.tip_3 !== undefined) tipPercentages.push(values.tip_3);

    const request: UpdateRestaurantPublicInfoRequest = {
      address: values.address || undefined,
      open_second: values.open_time
        ? values.open_time.hour() * 3600 + values.open_time.minute() * 60
        : null,
      close_second: values.close_time
        ? values.close_time.hour() * 3600 + values.close_time.minute() * 60
        : null,
      instagram_url: values.instagram_url || null,
      google_url: values.google_url || null,
      yelp_url: values.yelp_url || null,
      facebook_url: values.facebook_url || null,
      default_tip_percentages: tipPercentages.length > 0 ? tipPercentages : null,
      order_url: values.order_url || null,
      delivery_url: values.delivery_url || null,
      listed: values.listed ?? false,
      coming_soon: values.coming_soon ?? false,
    };

    fetchT<{ restaurant_public_info: RestaurantPublicInfo }>(
      "PUT",
      `${API_URL}/v1/r/${restaurantId}/admin/restaurant_public_info`,
      request,
      () => {
        updateSuccess(t("restaurant_public_info"));
        setLoading(false);
        refreshInfo();
      },
      (err) => {
        notifyError(t("failed_to_update_restaurant_public_info"), err);
        setLoading(false);
      }
    );
  };

  if (info === undefined) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <ShopOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("restaurant_public_info")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="address" label={t("address")}>
            <Input placeholder={t("enter_address")} />
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <ClockCircleOutlined />
              {t("business_hours")}
            </Space>
          </Divider>

          <Space size="large" style={{ display: "flex", flexWrap: "wrap" }}>
            <Form.Item name="open_time" label={t("open_time")}>
              <TimePicker format="HH:mm" placeholder={t("select_open_time")} />
            </Form.Item>

            <Form.Item name="close_time" label={t("close_time")}>
              <TimePicker format="HH:mm" placeholder={t("select_close_time")} />
            </Form.Item>
          </Space>

          <Divider orientation="left">
            <Space>
              <LinkOutlined />
              {t("social_media_links")}
            </Space>
          </Divider>

          <Form.Item name="instagram_url" label={t("instagram_url")}>
            <Input placeholder={t("enter_instagram_url")} />
          </Form.Item>

          <Form.Item name="google_url" label={t("google_url")}>
            <Input placeholder={t("enter_google_url")} />
          </Form.Item>

          <Form.Item name="yelp_url" label={t("yelp_url")}>
            <Input placeholder={t("enter_yelp_url")} />
          </Form.Item>

          <Form.Item name="facebook_url" label={t("facebook_url")}>
            <Input placeholder={t("enter_facebook_url")} />
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <LinkOutlined />
              {t("ordering_links")}
            </Space>
          </Divider>

          <Form.Item name="order_url" label={t("order_url")} extra={t("order_url_help")}>
            <Input placeholder={t("enter_order_url")} />
          </Form.Item>

          <Form.Item name="delivery_url" label={t("delivery_url")} extra={t("delivery_url_help")}>
            <Input placeholder={t("enter_delivery_url")} />
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <EyeOutlined />
              {t("public_listing")}
            </Space>
          </Divider>

          <Form.Item
            name="listed"
            label={t("listed")}
            valuePropName="checked"
            extra={t("listed_help")}
          >
            <Switch />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.listed !== cur.listed}>
            {({ getFieldValue }) => (
              <Form.Item
                name="coming_soon"
                label={t("coming_soon")}
                valuePropName="checked"
                extra={t("coming_soon_help")}
                tooltip={!getFieldValue("listed") ? t("coming_soon_requires_listed") : undefined}
              >
                <Switch disabled={!getFieldValue("listed")} />
              </Form.Item>
            )}
          </Form.Item>

          <Divider orientation="left">
            <Space>
              <DollarOutlined />
              {t("default_tip_percentages")}
            </Space>
          </Divider>

          <Space size="large" style={{ display: "flex", flexWrap: "wrap" }}>
            <Form.Item name="tip_1" label={t("tip_option_1")}>
              <InputNumber min={0} max={100} placeholder="%" addonAfter="%" />
            </Form.Item>

            <Form.Item name="tip_2" label={t("tip_option_2")}>
              <InputNumber min={0} max={100} placeholder="%" addonAfter="%" />
            </Form.Item>

            <Form.Item name="tip_3" label={t("tip_option_3")}>
              <InputNumber min={0} max={100} placeholder="%" addonAfter="%" />
            </Form.Item>
          </Space>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("save")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
